import { useState } from 'react'
import { StandardLoader } from '../StandardLoader'
import { PasswordInput } from '../PasswordInput'
import { Button } from '@duck/ui'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useTar, TypedTrans } from '@/modules'
import { generateSlug } from '@/utils/slugger-client'

interface EmailLoginFormProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>
  isLoading: boolean
  isDisabled: boolean
  onForgotPassword?: () => void
  onSignUp?: () => void
}

const hideSignup = true

export const EmailLoginForm = ({
  onSubmit,
  isLoading,
  isDisabled,
  onForgotPassword,
  onSignUp
}: EmailLoginFormProps) => {
  const { t } = useTar()
  const [step, setStep] = useState<'email' | 'password'>('email')
  const [email, setEmail] = useState('')
  const [emailPlaceholder] = useState(() => generateSlug())

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (step === 'email') {
      e.preventDefault()
      if (!e.currentTarget.reportValidity()) return

      const formData = new FormData(e.currentTarget)
      const newEmail = String(formData.get('email') ?? '')
      setEmail(newEmail)

      setStep('password')
      return
    }

    return onSubmit(e)
  }

  return (
    <>
      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center items-center text-sm h-8 gap-1">
          <div className='bg-card px-2 flex justify-center items-center '>
            <span className="px-2 text-muted">
              {step === 'email' ? (
                t('auth.loginWithEmail')
              ) : (
                <TypedTrans
                  i18nKey="auth.loginWithEMAIL"
                  values={{ email }}
                  components={[
                    <span key="email" className="text-primary font-medium" />,
                  ]}
                />
              )}
            </span>
            {step === 'password' && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="bg-card h-4 w-4 shrink-0 rounded-full text-muted hover:text-foreground"
                onClick={() => setStep('email')}
                disabled={isDisabled}
                aria-label={t('common.back')}
              >
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Email Sign-In Form */}
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
        {/* Always render both fields so password managers can detect them from step one.
            We only visually hide the non-active field. */}
        <div className={step === 'password' ? 'hidden' : ''}>
          <label htmlFor="email" className="block text-sm font-medium text-foreground my-2">
            {t('auth.emailAddress')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="username"
            required
            disabled={isDisabled}
            className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground
                       placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                       focus:border-accent transition-colors
                       ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            placeholder={emailPlaceholder}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className={step === 'email' ? 'hidden' : ''}>
          <label htmlFor="password" className="block text-sm font-medium text-foreground my-2">
            {t('common.password')}
          </label>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            required={step === 'password'}
            disabled={isDisabled}
            placeholder={t('auth.passwordPlaceholder')}
          />
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              disabled={isDisabled}
              name="rememberMe"
              className="h-4 w-4 text-accent focus:ring-ring border-border rounded"
            />
            <span className="ms-2 text-sm text-muted-foreground">{t('common.rememberMe')}</span>
          </label>
          {onForgotPassword && (
            <button
              type="button"
              disabled={isDisabled}
              onClick={onForgotPassword}
              className={`text-sm transition-colors
                ${isDisabled
                  ? 'text-muted cursor-not-allowed'
                  : 'text-accent hover:text-foreground'
                }`}
            >
              {t('common.forgotPassword')}
            </button>
          )}
        </div>

        <button
          type="submit"
          disabled={isDisabled}
          className={`w-full px-4 py-2 rounded-lg font-medium
                   focus:outline-none focus:ring-2 focus:ring-ring 
                   transition-colors duration-200 flex items-center justify-center
                   ${isDisabled
              ? 'bg-accent/50 text-white cursor-not-allowed opacity-75'
              : 'bg-accent text-white hover:bg-accent/90'
            }`}
        >
          {isLoading ? (
            <>
              <StandardLoader size="sm" inline />
              <span>{t('common.loading')}</span>
            </>
          ) : (
            t('common.login')
          )}
        </button>
      </form>

      {/* Footer */}
      {onSignUp && !hideSignup && (
        <div className="text-center mt-4">
          <p className="text-sm text-muted-foreground">
            {t('common.newToAppName')}{' '}
            <button
              onClick={onSignUp}
              className={`font-medium transition-colors
                ${isDisabled
                  ? 'text-muted cursor-not-allowed'
                  : 'text-accent hover:text-foreground'
                }`}
              disabled={isDisabled}
            >
              {t('common.joinAppName')}
            </button>
          </p>
        </div>
      )}
    </>
  )
} 